/**
 * Landing Page - The Gateway to CodeForge
 * ========================================
 * This is what visitors see first!
 * It should look COOL and make them want to join.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Server, 
  Code2, 
  Database, 
  Rocket, 
  Shield, 
  Zap,
  Terminal,
  Container,
  Brain,
  ChevronRight,
  Github
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Container className="w-8 h-8" />,
      title: "DOCKER DEPLOYMENTS",
      description: "Launch portable containers with Debian 12 + PHP 8.2 in seconds",
      color: "text-blue-400",
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "POSTGRESQL + ML",
      description: "Built-in PostgresML for machine learning right in your database",
      color: "text-green-400",
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "CODE EDITOR",
      description: "Browser-based editor with syntax highlighting for PHP, Python, C",
      color: "text-primary",
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "ML STUDIO",
      description: "Train models, make predictions, all from your browser",
      color: "text-purple-400",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "SECURE BY DEFAULT",
      description: "Isolated containers, encrypted connections, safe environment",
      color: "text-red-400",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "INSTANT SCALING",
      description: "Start small, grow big. Resources scale with your needs",
      color: "text-yellow-400",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B0D10] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1616506436028-1980ead3ebe7?crop=entropy&cs=srgb&fm=jpg&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D10] via-transparent to-[#0B0D10]" />
      
      {/* Navigation */}
      <nav className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Terminal className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              CODEFORGE
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-muted-foreground hover:text-white"
              onClick={() => navigate("/auth")}
              data-testid="login-nav-btn"
            >
              LOGIN
            </Button>
            <Button 
              className="bg-primary text-black font-bold hover:bg-primary/90"
              onClick={() => navigate("/auth?mode=register")}
              data-testid="signup-nav-btn"
            >
              GET STARTED
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-mono text-primary uppercase tracking-wider">
              SYSTEMS ONLINE
            </span>
          </div>
          
          {/* Main Headline */}
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter mb-6"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="hero-title"
          >
            BUILD.
            <span className="text-primary"> BREAK.</span>
            <br />
            DEPLOY.
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 font-mono">
            The mecha-garage for young hackers. Deploy Docker containers, 
            write PHP code, train ML models. All in one place.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-primary text-black font-bold text-lg px-8 py-6 hover:bg-primary/90 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)] active:translate-y-[2px] active:shadow-none transition-all"
              onClick={() => navigate("/auth?mode=register")}
              data-testid="hero-cta-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />
              START YOUR GARAGE
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white/20 text-white font-bold text-lg px-8 py-6 hover:bg-white/5"
              onClick={() => navigate("/auth")}
              data-testid="hero-login-btn"
            >
              <Terminal className="w-5 h-5 mr-2" />
              ACCESS TERMINAL
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-xl mx-auto mt-16">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                99.9%
              </div>
              <div className="text-xs text-muted-foreground font-mono uppercase mt-1">UPTIME</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                50ms
              </div>
              <div className="text-xs text-muted-foreground font-mono uppercase mt-1">DEPLOY TIME</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-green-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                PHP 8.2
              </div>
              <div className="text-xs text-muted-foreground font-mono uppercase mt-1">LATEST</div>
            </div>
          </div>

          {/* Domain Badge */}
          <div className="mt-12 inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-xs font-mono text-green-400">
              NOW LIVE AT ai.daniejoye.com
            </span>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            YOUR TOOLKIT
          </h2>
          <p className="text-muted-foreground font-mono">Everything you need to build amazing things</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group bg-[#16191F] border border-white/5 p-6 hover:border-primary/50 transition-all duration-200 cursor-pointer card-hover"
              data-testid={`feature-card-${index}`}
            >
              <div className={`${feature.color} mb-4`}>
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2 tracking-wide" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-sm text-muted-foreground font-mono">
                {feature.description}
              </p>
              <div className="mt-4 flex items-center text-primary text-sm font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Frick & Frack Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-yellow-400/10 via-orange-500/10 to-red-500/10 border border-primary/20 p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="text-xs font-mono text-primary uppercase tracking-wider mb-2">
                A FAMILY OF HACKERS
              </div>
              <h3 
                className="text-2xl md:text-3xl font-bold text-white mb-4"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                MEET THE TEAM
              </h3>
              <p className="text-muted-foreground font-mono mb-6">
                CodeForge was built by a family of makers. 
                <span className="text-primary"> Frick</span> (age 8) is the curious architect who asks "why?" 
                <span className="text-green-400"> Frack</span> (age 10) is the operator who makes things run.
                <span className="text-blue-400"> Bubba</span> (their wiser dad) guides the mission and keeps the garage running.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 bg-primary rounded-full" />
                  <span className="text-white font-mono">FRICK - The Architect</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 bg-green-400 rounded-full" />
                  <span className="text-white font-mono">FRACK - The Operator</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 bg-blue-400 rounded-full" />
                  <span className="text-white font-mono">BUBBA - The Mentor</span>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="w-40 h-40 bg-[#16191F] border-2 border-primary/30 flex items-center justify-center">
                <Code2 className="w-20 h-20 text-primary" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center">
          <h2 
            className="text-3xl md:text-5xl font-black text-white tracking-tight mb-6"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            READY TO BUILD?
          </h2>
          <p className="text-muted-foreground font-mono mb-8 max-w-xl mx-auto">
            Join the mecha-garage. Deploy your first site in under 60 seconds.
          </p>
          <Button
            size="lg"
            className="bg-primary text-black font-bold text-lg px-12 py-6 hover:bg-primary/90"
            onClick={() => navigate("/auth?mode=register")}
            data-testid="cta-final-btn"
          >
            ENTER THE GARAGE
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm font-mono">
            <Terminal className="w-4 h-4" />
            <span>CodeForge v1.0.0 | ai.daniejoye.com</span>
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            Built by Bubba, Frick & Frack - A Family of Hackers
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
